import * as React from "react";
import { useDiffStore } from "@/features/sources/state/diffStore";
import { useShallow } from "zustand/shallow";
import {
  selectActiveActions,
  useActiveActionStore,
  type ActiveAction,
} from "../state/activeActionStore";
import { resolveTabForActionType } from "../actionTypeToTab";
import type { WorkspaceTab } from "../../workspace-router/state/workspaceStore";

/**
 * useActiveAction — read-side hook for surfaces that need the live HITL
 * proposals routed for the current workspace tab.
 *
 * Decoupled from the WS listener (`useActionRouter`) so unit tests can
 * inject fixture actions into the store and assert the surface filters
 * them correctly.
 *
 * ``useShallow`` is mandatory here: ``selectActiveActions`` rebuilds the
 * derived array on every snapshot read (the store holds a keyed map +
 * ordered ids — the array projection is computed). Without
 * ``useShallow`` React 19 + Zustand 5 trip ``Maximum update depth
 * exceeded`` because each ``useSyncExternalStore`` snapshot returns a
 * new array reference → React schedules a re-render → snapshot fires
 * again → loop. ``useShallow`` does element-wise equality so a stable
 * action set short-circuits the cycle. Caught May 23, 2026 at the
 * ``ActionFormSurface`` mount.
 */
export function useActiveActionsForTab(tab: WorkspaceTab): readonly ActiveAction[] {
  const all = useActiveActionStore(useShallow(selectActiveActions));
  return React.useMemo(
    () => all.filter((a) => resolveTabForActionType(a.actionType) === tab),
    [all, tab],
  );
}

/**
 * Cross-tab pending proposals — every proposal still awaiting the
 * user's decision regardless of which workspace tab is currently
 * mounted. Drives the persistent "Proposte aperte" chip row at the
 * top of the work area so the user can hop between, e.g., an open
 * ``document_create`` (tab Documenti) and a freshly arrived
 * ``send_email`` (tab Conversazioni) without scrolling chat back to
 * the originating message. ``executed`` / ``error`` proposals stay
 * in the store (history) but drop out of this list — they are no
 * longer awaiting input. May 25, 2026 — explicit user feedback "ho
 * perso il tab per ritornare alla generazione del file prima".
 */
const PENDING_STATUSES: ReadonlySet<string> = new Set([
  "proposed",
  "editing",
  "confirming",
]);

export function useAllPendingActions(): readonly ActiveAction[] {
  const all = useActiveActionStore(useShallow(selectActiveActions));
  return React.useMemo(
    () => all.filter((a) => PENDING_STATUSES.has(a.status)),
    [all],
  );
}

/** Returns the single action with `id`, or `undefined`. */
export function useActiveAction(id: string | undefined): ActiveAction | undefined {
  return useActiveActionStore((s) =>
    id ? s.actions[id] : undefined,
  );
}

/** Imperative store API for components outside the React render tree. */
export const activeActionApi = {
  upsert: (action: ActiveAction) => useActiveActionStore.getState().upsert(action),
  patch: (id: string, patch: Partial<ActiveAction>) => {
    useActiveActionStore.getState().patch(id, patch);
    if (patch.status === "executed") {
      useDiffStore.getState().clearForProposal(id);
    }
  },
  remove: (id: string) => {
    useActiveActionStore.getState().remove(id);
    useDiffStore.getState().clearForProposal(id);
  },
} as const;
